/**
 * useCommunicationTraceDashboard — deterministic state machine for the
 * Communication Trace dashboard (ODE-P10-T07).
 *
 * Flows:
 *   summary   pending → (token + driverId + load) → loading → ready | empty |
 *             unauthorized (401) | blocked (403) | unavailable (503) | error
 *   detail    idle → gated (purpose/case form) → loading → ready | fail-closed
 *   search    idle → gated (purpose/case form) → loading → ready | fail-closed
 *   export    idle → gated (purpose/case form) → loading → ready | fail-closed
 *
 * Every gated action (detail/search/export) requires an approved purpose code
 * and, when provided, a case/reference before the request is sent (REQ-101);
 * the T06 backend additionally audits every success/denial (REQ-102) and
 * remains the authorization authority (defense in depth). The trace access
 * token lives in memory only and is never persisted or logged. All flows are
 * single-flight: stale responses are ignored, so concurrent/retry attempts
 * can never create contradictory state (TEST05).
 *
 * Traces to: ODE-PRD-REQ-098, ODE-PRD-REQ-099, ODE-PRD-REQ-100,
 *            ODE-PRD-REQ-101, ODE-PRD-REQ-110.
 */

import { useCallback, useRef, useState } from 'react';
import type { CommunicationTraceGateway, DetailQuery, ExportRequest, SearchQuery } from './gateway.js';
import {
  DEFAULT_TRACE_FILTERS,
  type ContactDetail,
  type ExportBundle,
  type PurposeCode,
  type SearchResults,
  type TraceActionState,
  type TraceSummaryState,
  type TraceViewFilters,
} from './types.js';

interface PendingDetail {
  readonly contactKey: string;
}

interface PendingSearch {
  readonly q: string;
}

function isEmptySummary(summary: {
  readonly contacts: readonly unknown[];
  readonly calls: { readonly totalCount: number };
  readonly whatsapp: { readonly totalNotificationCount: number };
}): boolean {
  return summary.contacts.length === 0 && summary.calls.totalCount === 0 && summary.whatsapp.totalNotificationCount === 0;
}

export function useCommunicationTraceDashboard(gateway: CommunicationTraceGateway) {
  const [token, setTokenState] = useState('');
  const [driverId, setDriverId] = useState('');
  const [filters, setFilters] = useState<TraceViewFilters>(DEFAULT_TRACE_FILTERS);
  const [summaryState, setSummaryState] = useState<TraceSummaryState>({ kind: 'pending' });
  const [detailState, setDetailState] = useState<TraceActionState<ContactDetail>>({ kind: 'idle' });
  const [searchState, setSearchState] = useState<TraceActionState<SearchResults>>({ kind: 'idle' });
  const [exportState, setExportState] = useState<TraceActionState<ExportBundle>>({ kind: 'idle' });
  const [pendingDetail, setPendingDetail] = useState<PendingDetail | null>(null);
  const [pendingSearch, setPendingSearch] = useState<PendingSearch | null>(null);
  const [pendingExport, setPendingExport] = useState(false);

  const summaryAttempt = useRef(0);
  const detailAttempt = useRef(0);
  const searchAttempt = useRef(0);
  const exportAttempt = useRef(0);

  const setToken = useCallback((value: string) => {
    setTokenState(value);
  }, []);

  const updateFilters = useCallback((patch: Partial<TraceViewFilters>) => {
    setFilters((current) => ({ ...current, ...patch }));
  }, []);

  /** Summary flow — single-flight; stale responses are ignored. */
  const loadSummary = useCallback(() => {
    const attempt = summaryAttempt.current + 1;
    summaryAttempt.current = attempt;
    if (token.trim() === '') {
      setSummaryState({ kind: 'pending' });
      return;
    }
    if (driverId.trim() === '') {
      setSummaryState({ kind: 'blocked', message: 'driverId wajib diisi sebelum memuat ringkasan.' });
      return;
    }
    setSummaryState({ kind: 'loading' });
    gateway
      .fetchSummary(
        {
          driverId: driverId.trim(),
          window: filters.window,
          start: filters.start,
          end: filters.end,
        },
        token.trim(),
      )
      .then((outcome) => {
        if (summaryAttempt.current !== attempt) return; // stale — a newer attempt owns the state
        if (outcome.kind === 'ok') {
          setSummaryState(
            isEmptySummary(outcome.payload)
              ? { kind: 'empty', summary: outcome.payload }
              : { kind: 'ready', summary: outcome.payload },
          );
          return;
        }
        switch (outcome.status) {
          case 401:
            setSummaryState({ kind: 'unauthorized' });
            break;
          case 403:
            setSummaryState({ kind: 'blocked', message: withCode(outcome.code, outcome.message) });
            break;
          case 503:
            setSummaryState({ kind: 'unavailable', message: withCode(outcome.code, outcome.message) });
            break;
          default:
            setSummaryState({ kind: 'error', message: withCode(outcome.code, outcome.message) });
        }
      })
      .catch((error: unknown) => {
        if (summaryAttempt.current !== attempt) return;
        setSummaryState({
          kind: 'error',
          message: error instanceof Error ? error.message : 'Kesalahan jaringan',
        });
      });
  }, [token, driverId, filters, gateway]);

  /** Opens the purpose/case gate for a contact detail. */
  const requestDetail = useCallback((contactKey: string) => {
    setPendingDetail({ contactKey });
    setDetailState({ kind: 'gated' });
  }, []);

  /** Opens the purpose/case gate for search. */
  const requestSearch = useCallback((q: string) => {
    setPendingSearch({ q });
    setSearchState({ kind: 'gated' });
  }, []);

  /** Opens the purpose/case gate for export. */
  const requestExport = useCallback(() => {
    setPendingExport(true);
    setExportState({ kind: 'gated' });
  }, []);

  const cancelDetail = useCallback(() => {
    detailAttempt.current += 1;
    setPendingDetail(null);
    setDetailState({ kind: 'idle' });
  }, []);

  const cancelSearch = useCallback(() => {
    searchAttempt.current += 1;
    setPendingSearch(null);
    setSearchState({ kind: 'idle' });
  }, []);

  const cancelExport = useCallback(() => {
    exportAttempt.current += 1;
    setPendingExport(false);
    setExportState({ kind: 'idle' });
  }, []);

  /** Executes the pending gated action with the captured purpose/case (REQ-101). */
  const confirmPurposeCase = useCallback(
    (purposeCode: PurposeCode, caseRef: string) => {
      if (token.trim() === '') {
        setSummaryState({ kind: 'unauthorized' });
        return;
      }
      const base = {
        driverId: driverId.trim(),
        window: filters.window,
        start: filters.start,
        end: filters.end,
      };
      const normalizedCaseRef = caseRef.trim() === '' ? undefined : caseRef.trim();

      if (pendingDetail !== null) {
        const attempt = detailAttempt.current + 1;
        detailAttempt.current = attempt;
        setDetailState({ kind: 'loading' });
        const query: DetailQuery = {
          ...base,
          contactKey: pendingDetail.contactKey,
        };
        gateway
          .fetchContactDetail(query, token.trim())
          .then((outcome) => {
            if (detailAttempt.current !== attempt) return;
            if (outcome.kind === 'ok') {
              setDetailState({ kind: 'ready', payload: outcome.payload });
              return;
            }
            setDetailState(actionFailure(outcome.status, outcome.code, outcome.message));
          })
          .catch((error: unknown) => {
            if (detailAttempt.current !== attempt) return;
            setDetailState({ kind: 'error', message: error instanceof Error ? error.message : 'Kesalahan jaringan' });
          });
        return;
      }

      if (pendingSearch !== null) {
        const attempt = searchAttempt.current + 1;
        searchAttempt.current = attempt;
        setSearchState({ kind: 'loading' });
        const query: SearchQuery = {
          ...base,
          q: pendingSearch.q,
          purposeCode,
          caseRef: normalizedCaseRef,
        };
        gateway
          .search(query, token.trim())
          .then((outcome) => {
            if (searchAttempt.current !== attempt) return;
            if (outcome.kind === 'ok') {
              setSearchState({ kind: 'ready', payload: outcome.payload });
              return;
            }
            setSearchState(actionFailure(outcome.status, outcome.code, outcome.message));
          })
          .catch((error: unknown) => {
            if (searchAttempt.current !== attempt) return;
            setSearchState({ kind: 'error', message: error instanceof Error ? error.message : 'Kesalahan jaringan' });
          });
        return;
      }

      if (pendingExport) {
        const attempt = exportAttempt.current + 1;
        exportAttempt.current = attempt;
        setExportState({ kind: 'loading' });
        const request: ExportRequest = {
          driverId: base.driverId,
          window: base.window,
          start: base.start,
          end: base.end,
          purposeCode,
          caseRef: normalizedCaseRef,
        };
        gateway
          .exportBundle(request, token.trim())
          .then((outcome) => {
            if (exportAttempt.current !== attempt) return;
            if (outcome.kind === 'ok') {
              setExportState({ kind: 'ready', payload: outcome.payload });
              return;
            }
            setExportState(actionFailure(outcome.status, outcome.code, outcome.message));
          })
          .catch((error: unknown) => {
            if (exportAttempt.current !== attempt) return;
            setExportState({ kind: 'error', message: error instanceof Error ? error.message : 'Kesalahan jaringan' });
          });
      }
    },
    [token, driverId, filters, gateway, pendingDetail, pendingSearch, pendingExport],
  );

  return {
    token,
    setToken,
    driverId,
    setDriverId,
    filters,
    updateFilters,
    summaryState,
    loadSummary,
    detailState,
    requestDetail,
    cancelDetail,
    searchState,
    requestSearch,
    cancelSearch,
    exportState,
    requestExport,
    cancelExport,
    confirmPurposeCase,
  };
}

function actionFailure<T>(status: number, code: string, message: string): TraceActionState<T> {
  switch (status) {
    case 401:
      return { kind: 'unauthorized' };
    case 403:
      return { kind: 'blocked', message: withCode(code, message) };
    case 503:
      return { kind: 'unavailable', message: withCode(code, message) };
    default:
      return { kind: 'error', message: withCode(code, message) };
  }
}

/** Surfaces the backend error code in the message so operators see e.g. POLICY_GATE_BLOCKED. */
function withCode(code: string, message: string): string {
  if (code === '' || code === 'UNKNOWN') return message;
  return `[${code}] ${message}`;
}